import React, { useEffect } from 'react';
import { Box, Stack, Typography, IconButton, Button, Select, MenuItem, FormControl } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import {
  ChartContainer, ChartsReferenceLine, ChartsXAxis, ChartsYAxis, LinePlot,
} from '@mui/x-charts';
import dayjs from 'dayjs';
import { useConfig } from '../../components/ConfigContext';

const cyan = 'var(--v4-cyan)';
const purple = 'var(--v4-purple)';

const cardSx = {
  background: 'rgba(var(--v4-surface-rgb),0.88)',
  border: '1px solid rgba(var(--v4-cyan-rgb),0.12)',
  borderRadius: '18px',
  backdropFilter: 'blur(20px)',
  p: 3,
};

const labelSx = {
  fontSize: '0.58rem',
  fontWeight: 800,
  textTransform: 'uppercase',
  letterSpacing: '0.15em',
  color: 'rgba(var(--v4-cyan-rgb),0.5)',
  mb: 0.5,
  display: 'block',
};

const stepBtnSx = {
  width: 32,
  height: 32,
  border: '1px solid rgba(var(--v4-cyan-rgb),0.28)',
  borderRadius: '50%',
  color: cyan,
  background: 'rgba(var(--v4-cyan-rgb),0.05)',
  p: 0,
  '&:hover': { background: 'rgba(var(--v4-cyan-rgb),0.14)', borderColor: cyan },
  '&:disabled': { opacity: 0.3 },
};

const numInputStyle = {
  width: '5rem',
  textAlign: 'center',
  background: 'rgba(var(--v4-cyan-rgb),0.04)',
  border: '1px solid rgba(var(--v4-cyan-rgb),0.14)',
  borderRadius: 8,
  color: 'rgba(var(--v4-text-rgb),0.92)',
  fontSize: '1rem',
  fontWeight: 700,
  fontFamily: 'Inter, Roboto, sans-serif',
  padding: '7px 10px',
  outline: 'none',
};

const numInputReadOnly = {
  ...numInputStyle,
  opacity: 0.5,
  cursor: 'not-allowed',
  color: 'rgba(var(--v4-text-rgb),0.55)',
};

const selectSx = {
  color: 'rgba(var(--v4-text-rgb),0.88)',
  fontWeight: 600,
  borderRadius: '12px',
  background: 'rgba(var(--v4-cyan-rgb),0.04)',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(var(--v4-cyan-rgb),0.18)' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(var(--v4-cyan-rgb),0.38)' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: cyan },
  '& .MuiSelect-icon': { color: 'rgba(var(--v4-cyan-rgb),0.55)' },
};

function saturationVaporPressureKPa(tC) {
  return 0.6108 * Math.exp((17.27 * tC) / (tC + 237.3));
}

function rhFromVpd(tC, vpdKPa) {
  const svp = saturationVaporPressureKPa(tC);
  if (!Number.isFinite(svp) || svp <= 0) return 0;
  return Math.max(0, Math.min(100, 100 * (1 - vpdKPa / svp)));
}

function ReadOnlyDisplay({ label, value }) {
  return (
    <Box sx={{ background: 'rgba(var(--v4-cyan-rgb),0.03)', border: '1px solid rgba(var(--v4-cyan-rgb),0.08)', borderRadius: '12px', p: 2 }}>
      <Typography component="span" sx={labelSx}>{label}</Typography>
      <Stack direction="row" alignItems="center" spacing={1}>
        <input value={value ?? ''} readOnly style={numInputReadOnly} />
        <Typography sx={{ fontSize: '0.75rem', color: 'rgba(var(--v4-cyan-rgb),0.4)', fontWeight: 600 }}>%</Typography>
      </Stack>
    </Box>
  );
}

function V4Stepper({ value, onChange, min = 0, max = 10, step = 0.5 }) {
  const dec = () => onChange(Math.max(min, (value ?? 0) - step));
  const inc = () => onChange(Math.min(max, (value ?? 0) + step));
  const handleChange = e => {
    const v = parseFloat(e.target.value);
    if (!isNaN(v)) onChange(Math.max(min, Math.min(max, v)));
  };
  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <IconButton onClick={dec} sx={stepBtnSx} size="small"><RemoveIcon sx={{ fontSize: 16 }} /></IconButton>
      <input value={value ?? ''} onChange={handleChange} style={numInputStyle} />
      <IconButton onClick={inc} sx={stepBtnSx} size="small"><AddIcon sx={{ fontSize: 16 }} /></IconButton>
    </Stack>
  );
}

export default function V4ConfigAbluft({ is_managed }) {
  const { state, dispatch, saveConfig } = useConfig();

  const phase     = state.config.vpd_phase ?? 'veg';
  const targetVpd = phase === 'flower' ? 1.4 : 1.0;

  useEffect(() => {
    if (!state.config.vpd_phase) {
      dispatch({ type: 'SET_FIELD', field: 'vpd_phase', value: 'veg' });
    }
  }, [state.config.vpd_phase, dispatch]);

  useEffect(() => {
    const tDay   = parseFloat(state.config.temperatur_tag);
    const tNight = parseFloat(state.config.temperatur_nacht);
    if (!Number.isFinite(tDay) || !Number.isFinite(tNight)) return;

    const rhDay   = Math.round(rhFromVpd(tDay, targetVpd));
    const rhNight = Math.round(rhFromVpd(tNight, targetVpd));

    if (Number.isFinite(rhDay)   && rhDay   !== parseInt(state.config.luftfeuchte_tag))
      dispatch({ type: 'SET_FIELD', field: 'luftfeuchte_tag',   value: rhDay });
    if (Number.isFinite(rhNight) && rhNight !== parseInt(state.config.luftfeuchte_nacht))
      dispatch({ type: 'SET_FIELD', field: 'luftfeuchte_nacht', value: rhNight });
  }, [phase, targetVpd, state.config.temperatur_tag, state.config.temperatur_nacht,
      state.config.luftfeuchte_tag, state.config.luftfeuchte_nacht, dispatch]);

  return (
    <Box sx={cardSx}>
      {/* Phase selector */}
      <Box sx={{ mb: 3 }}>
        <Typography component="span" sx={labelSx}>Wachstumsphase</Typography>
        <FormControl size="small" fullWidth>
          <Select
            value={phase}
            onChange={e => dispatch({ type: 'SET_FIELD', field: 'vpd_phase', value: e.target.value })}
            IconComponent={KeyboardArrowDownRoundedIcon}
            sx={selectSx}
            MenuProps={{
              PaperProps: {
                sx: {
                  background: 'rgba(var(--v4-surface-rgb),0.97)',
                  border: '1px solid rgba(var(--v4-cyan-rgb),0.15)',
                  borderRadius: '12px',
                  backdropFilter: 'blur(20px)',
                  '& .MuiMenuItem-root': {
                    color: 'rgba(var(--v4-text-rgb),0.85)',
                    fontSize: '0.88rem',
                    fontWeight: 500,
                    '&:hover':    { background: 'rgba(var(--v4-cyan-rgb),0.08)' },
                    '&.Mui-selected': { background: 'rgba(var(--v4-cyan-rgb),0.12)', color: cyan },
                  },
                },
              },
            }}
          >
            <MenuItem value="veg">Wuchsphase — Ziel-VPD 1.0 kPa</MenuItem>
            <MenuItem value="flower">Blütephase — Ziel-VPD 1.4 kPa</MenuItem>
          </Select>
        </FormControl>
        <Typography sx={{ fontSize: '0.68rem', color: 'rgba(var(--v4-cyan-rgb),0.38)', mt: 0.75, fontWeight: 500 }}>
          Berechnung aus {state.config.temperatur_tag}°C / {state.config.temperatur_nacht}°C → Ziel-VPD {targetVpd} kPa
        </Typography>
      </Box>

      {/* RH + Abweichung */}
      <Stack direction="row" flexWrap="wrap" gap={2} useFlexGap>
        <ReadOnlyDisplay label="Luftfeuchte Tag (auto)" value={parseInt(state.config.luftfeuchte_tag)} />
        <ReadOnlyDisplay label="Luftfeuchte Nacht (auto)" value={parseInt(state.config.luftfeuchte_nacht)} />

        <Box sx={{ background: 'rgba(var(--v4-cyan-rgb),0.03)', border: '1px solid rgba(var(--v4-cyan-rgb),0.08)', borderRadius: '12px', p: 2 }}>
          <Typography component="span" sx={labelSx}>Erlaubte Abweichung</Typography>
          <V4Stepper
            value={parseFloat(state.config.abweichung_luftfeuchte)}
            onChange={v => dispatch({ type: 'SET_FIELD', field: 'abweichung_luftfeuchte', value: v })}
            min={0} max={10} step={0.5}
          />
        </Box>
      </Stack>

      {/* Chart */}
      <Box sx={{ mt: 3, borderRadius: '12px', overflow: 'hidden', border: '1px solid rgba(var(--v4-cyan-rgb),0.08)', background: 'rgba(var(--v4-shadow-rgb),calc(0.2*var(--v4-shadow-k)))', display: 'flex', justifyContent: 'center' }}>
        <ChartContainer
          series={[{ type: 'line', data: [] }]}
          xAxis={[{ data: [dayjs().startOf('day'), dayjs().endOf('day')], scaleType: 'band', id: 'x' }]}
          yAxis={[{ max: 100, min: 0, scaleType: 'linear', id: 'y' }]}
          width={500}
          height={180}
          sx={{
            '& .MuiChartsAxis-line, & .MuiChartsAxis-tick': { stroke: 'rgba(var(--v4-cyan-rgb),0.18)' },
            '& .MuiChartsAxis-tickLabel': { fill: 'rgba(var(--v4-muted-rgb),0.45)', fontSize: '0.68rem' },
            '& .MuiChartsAxis-label': { fill: 'rgba(var(--v4-muted-rgb),0.45)' },
            background: 'transparent',
          }}
        >
          <LinePlot />
          <ChartsXAxis position="bottom" axisId="x" />
          <ChartsYAxis label="%" position="left" axisId="y" />
          <ChartsReferenceLine
            y={state.config.luftfeuchte_tag}
            label="Tag"
            axisId="y"
            lineStyle={{ stroke: cyan, strokeWidth: 1.5, strokeDasharray: '4 3' }}
            labelStyle={{ fill: 'rgba(var(--v4-cyan-rgb),0.7)', fontSize: '0.7rem' }}
          />
          <ChartsReferenceLine
            y={state.config.luftfeuchte_nacht}
            label="Nacht"
            axisId="y"
            lineStyle={{ stroke: purple, strokeWidth: 1.5, strokeDasharray: '4 3' }}
            labelStyle={{ fill: 'rgba(var(--v4-purple-rgb),0.7)', fontSize: '0.7rem' }}
          />
        </ChartContainer>
      </Box>

      {!is_managed && (
        <Button
          fullWidth variant="contained" startIcon={<SaveRoundedIcon />}
          onClick={saveConfig}
          sx={{
            mt: 2.5, borderRadius: '12px', fontWeight: 700, py: 1.2,
            background: 'linear-gradient(135deg,var(--v4-cyan),var(--v4-cyan-deep))',
            boxShadow: '0 4px 18px rgba(var(--v4-cyan-rgb),0.28)',
            '&:hover': { boxShadow: '0 6px 24px rgba(var(--v4-cyan-rgb),0.4)' },
          }}
        >
          Speichern
        </Button>
      )}
    </Box>
  );
}
